import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ButtonOne from "../../common/normal-button";
import { ColorsConstants } from "../../cssUtils/constants";
import typography from "../../cssUtils/typography";
import AppChip from "../../common/app-chip";
import TableSearchInput from "../TableSearchInput";

const dummyDepartments = [
  {
    id: 1,
    name: "Admin",
    createdOn: "06 Feb 2026, 06:03 PM",
    editedOn: "06 Feb 2026, 06:03 PM",
    description: "This is the default department",
  },
  {
    id: 2,
    name: "Chat Agent",
    createdOn: "06 Feb 2026, 06:03 PM",
    editedOn: "06 Feb 2026, 06:03 PM",
    description: null,
  },
  {
    id: 3,
    name: "Campaign Checker",
    createdOn: "06 Feb 2026, 06:03 PM",
    editedOn: "06 Feb 2026, 06:03 PM",
    description: null,
  },
];

const Departments = (props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchCharCount, setSearchCharCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const handleMenuOpen = (event, department) => {
    setAnchorEl(event.currentTarget);
    setSelectedDepartment(department);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDepartment(null);
  };

  const handleSearchChange = (value) => {
    if (value.length > 50) return;
    setSearchQuery(value);
    setSearchCharCount(value.length);
  };

  // Filter departments based on search query
  const filteredDepartments = dummyDepartments.filter((dept) =>
    dept.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (dept.description && dept.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <Box sx={{ backgroundColor: "#FAFAFA", minHeight: "100vh" }}>
      {/* Top Header Section - white background, full width, bottom border */}
      <Box
        sx={{
          backgroundColor: "#fff",
          borderBottom: "1px solid #E5E7EB",
          px: 3,
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 2,
          width: "100%",
        }}
      >
        {/* Search - takes remaining width */}
        <Box sx={{ flex: 1 }}>
          <TableSearchInput
            searchQuery={searchQuery}
            onSearchChange={(e) => handleSearchChange(e.target.value)}
            charCount={searchCharCount}
            tooltip="Search departments by name or description"
            placeholder="Search departments..."
          />
        </Box>

        {/* Button - fixed width */}
        <ButtonOne
          variant="contained"
          onClick={() => props.onNavigate && props.onNavigate("create_new_department")}
          sx={{
            whiteSpace: "nowrap",
            height: "40px",
            px: 3,
            ...typography.bodyLarge,
          }}
        >
          Create New Department
        </ButtonOne>
      </Box>

      {/* Cards Section */}
      <Box sx={{ p: 3 }}>
        {filteredDepartments.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography sx={{ ...typography.bodyLarge, color: "#6C7C93" }}>
              No departments found
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredDepartments.map((department) => (
              <Grid item xs={12} sm={6} md={4} key={department.id}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: "12px",
                    border: "1px solid #E5E7EB",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                    transition: "box-shadow 0.2s ease",
                    "&:hover": { boxShadow: "0px 4px 12px rgba(0,0,0,0.08)" },
                  }}
                >
                  {/* Card Header */}
                  <Box
                    sx={{
                      backgroundColor: "#F0F5FF",
                      px: 2,
                      py: 1.5,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        ...typography.bodyLarge,
                        fontWeight: 600,
                        color: ColorsConstants.primaryBackgroundColor,
                      }}
                    >
                      {department.name}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuOpen(e, department)}
                      sx={{
                        color: "#6C7C93",
                        "&:hover": { color: ColorsConstants.primaryBackgroundColor },
                      }}
                    >
                      <MoreVertIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Box>

                  {/* Card Body */}
                  <Box sx={{ p: 2 }}>
                    <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mb: 0.5 }}>
                      Created On
                    </Typography>
                    <Typography sx={{ ...typography.bodyLarge, color: "#04142F", mb: 2.5 }}>
                      {department.createdOn}
                    </Typography>

                    <Typography sx={{ ...typography.bodySmall, color: "#6C7C93", mb: 0.5 }}>
                      Edited on
                    </Typography>
                    <Typography sx={{ ...typography.bodyLarge, color: "#04142F", mb: 2.5 }}>
                      {department.editedOn}
                    </Typography>

                    {department.description && (
                      <Box
                        sx={{
                          borderTop: "1px solid #E2E8F0",
                          pt: 1.5,
                          mt: 1,
                        }}
                      >
                        <Typography
                          sx={{
                            ...typography.bodyLarge,
                            color: "#6C7C93",
                          }}
                        >
                          {department.description}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            border: "1px solid #DCDFE5",
            borderRadius: "8px",
            minWidth: 140,
            "& .MuiMenuItem-root": {
              ...typography.bodySmall,
              color: "#1D2330",
              py: 1,
              px: 2,
              "&:hover": { bgcolor: "#F0F4FA" },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem onClick={handleMenuClose}>
          <Typography sx={{ ...typography.bodySmall }}>Edit</Typography>
        </MenuItem>
        <MenuItem onClick={handleMenuClose}>
          <Typography sx={{ ...typography.bodySmall }}>Duplicate</Typography>
        </MenuItem>
        <MenuItem
          onClick={handleMenuClose}
          sx={{ color: "#E05252 !important" }}
        >
          <Typography sx={{ ...typography.bodySmall, color: "#E05252" }}>
            Delete
          </Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default Departments;